import { useRef, useCallback, ChangeEvent } from 'react';

import {
  Input,
  RadioInput,
  Select,
  Autocomplete,
  DatePicker,
} from '@new-components/elements';
import { useFormContext } from '@hooks/react-hook-form';
import {
  ApplicationDataResponse,
  useRGetApplicationData,
} from '@repositories/new-application';
import { useRGetRegencies } from '@repositories/common';

import ApplicationCard from '../../../../../application-card';
import NewApplicationAlert from 'pages/Home/NewApplication/components/application-alert/NewApplicationAlert';
import { PersonalDataProps } from './types';

import { email as emailValidator } from '@utils/validator';
import { useActionLog } from '@hooks/use-action-log';

const PersonalData = (props: PersonalDataProps) => {
  const { readonly } = props;
  const {
    fieldsRef,
    setError,
    clearErrors,
    formState: { isSubmitSuccessful, isSubmitting },
  } = useFormContext();

  const emailInputRef = useRef<HTMLInputElement>(null);

  useActionLog({ page: 'app_form_personal_data_page', view: !readonly });

  const { data: regenciesData, isInitialLoading: isLoadingGetRegencies } =
    useRGetRegencies({
      enabled: true,
    });

  const {
    data,
    isLoading: isLoadingGetApplicationData,
    isError: isErrorGetApplicationData,
    refetch: refetchGetApplicationData,
  } = useRGetApplicationData<ApplicationDataResponse<1>>({
    page: 1,
  });

  const handleEmailChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value;
      if (!emailValidator(value)) {
        setError('email', {
          message: 'Mohon masukkan alamat email yang valid',
        });
      } else {
        clearErrors('email');
      }
      e.target.removeAttribute('data-available-email');
    },
    [clearErrors, setError],
  );

  return (
    <ApplicationCard className='section-personal-data'>
      <NewApplicationAlert message='Pastikan informasi yang kamu masukkan sesuai dengan informasi yang tertera pada KTP.' />
      <Input
        ref={(e) => (fieldsRef.current['nik'] = e)}
        name='nik'
        label='Nomor KTP'
        disabled
      />
      <Input
        ref={(e) => (fieldsRef.current['email'] = e)}
        disabled={readonly || (isSubmitting && !isSubmitSuccessful)}
        name='email'
        label='Email'
        placeholder='email@example.com'
        onChange={handleEmailChange}
        inputRef={emailInputRef}
      />
      <Input
        ref={(e) => (fieldsRef.current['fullname'] = e)}
        disabled={readonly || (isSubmitting && !isSubmitSuccessful)}
        name='fullname'
        label='Nama Lengkap'
        placeholder='nama lengkap'
      />
      <DatePicker
        ref={(e) => (fieldsRef.current['dob'] = e)}
        name='dob'
        disabled={readonly || (isSubmitting && !isSubmitSuccessful)}
        label='Tanggal Lahir'
      />
      <Autocomplete
        ref={(e) => (fieldsRef.current['birthPlace'] = e)}
        name='birthPlace'
        label='Tempat Lahir'
        disabled={
          readonly ||
          isLoadingGetRegencies ||
          (isSubmitting && !isSubmitSuccessful)
        }
        options={(regenciesData?.data || []) as string[]}
      />
      <RadioInput
        ref={(e) => (fieldsRef.current['gender'] = e)}
        name='gender'
        label='Jenis Kelamin'
        flexOrientation='row'
        radioButtonProps={{ color: 'primary' }}
        disabled={readonly || (isSubmitting && !isSubmitSuccessful)}
        options={[
          { label: 'Pria', value: 'Pria' },
          { label: 'Wanita', value: 'Wanita' },
        ]}
      />
      <Select
        ref={(e) => (fieldsRef.current['lastEducation'] = e)}
        name='lastEducation'
        label='Pendidikan Terakhir'
        placeholder='pilih'
        disabled={
          readonly ||
          isLoadingGetApplicationData ||
          (isSubmitting && !isSubmitSuccessful)
        }
        onFocus={() => isErrorGetApplicationData && refetchGetApplicationData()}
        options={data?.data.lastEducations}
      />
      <Input
        ref={(e) => (fieldsRef.current['motherMaidenName'] = e)}
        disabled={readonly || (isSubmitting && !isSubmitSuccessful)}
        name='motherMaidenName'
        label='Nama Ibu Kandung'
      />
    </ApplicationCard>
  );
};

export default PersonalData;
